import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Coins, Plus, ArrowDown, ArrowUp, Gift, X, Ticket, Wallet as WalletIcon, ExternalLink, CheckCircle, AlertCircle, Crown, Shield, Code, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/Layout';
import { EmptyState } from '../components/EmptyState';
import { useAuth } from '../context/AuthContext';
import { useSolana } from '../context/WalletContext';
import { useToken } from '../context/TokenContext';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import { getLocalizedText } from '../components/MultiLanguageInput';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Wallet = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const { user, token, refreshUser } = useAuth();
  const { publicKey, connected, balance: solBalance, fetchBalance: fetchSolBalance, sendSOL, sendSOLViaContract, sendSOLWithCashback, loading: solLoading } = useSolana();
  const { getTokenSymbol, getTokenSymbolRaw } = useToken();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('coins'); // 'coins' or 'tickets'
  
  // Coin transactions
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Token transactions (was ticket)
  const [tokenTransactions, setTokenTransactions] = useState([]);
  const [tokenLoading, setTokenLoading] = useState(true);
  
  // Deposit dialog
  const [showDeposit, setShowDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [depositStep, setDepositStep] = useState('amount'); // 'amount', 'waiting_wallet', 'processing', 'success'
  
  // Solana settings
  const [solanaSettings, setSolanaSettings] = useState(null);
  const [solDepositStep, setSolDepositStep] = useState('amount'); // 'amount', 'confirm', 'processing', 'success', 'error'
  const [lastTxSignature, setLastTxSignature] = useState(null);
  const [depositError, setDepositError] = useState(null); // Store error for display
  
  // Item preview dialog
  const [selectedTx, setSelectedTx] = useState(null);
  const [showItemPreview, setShowItemPreview] = useState(false);
  
  // User level info for deposit bonus
  const [userLevelInfo, setUserLevelInfo] = useState(null);

  useEffect(() => {
    if (token) {
      fetchTransactions();
      fetchTokenTransactions();
    }
    fetchSolanaSettings();
    fetchUserLevelInfo();
  }, [token]);

  // Refresh SOL balance when connected
  useEffect(() => {
    if (connected) {
      fetchSolBalance();
    }
  }, [connected]);

  const fetchSolanaSettings = async () => {
    try {
      const res = await axios.get(`${API}/solana/settings`);
      setSolanaSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch Solana settings:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await axios.get(`${API}/wallet/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(res.data);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenTransactions = async () => {
    try {
      const res = await axios.get(`${API}/wallet/ticket-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTokenTransactions(res.data);
    } catch (err) {
      console.error('Failed to fetch token transactions:', err);
    } finally {
      setTokenLoading(false);
    }
  };

  const fetchUserLevelInfo = async () => {
    try {
      const res = await axios.get(`${API}/user/level`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserLevelInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch user level info:', err);
    }
  };

  // Calculate token bonus based on level and deposit amount
  const calculateTokenBonus = (coinsAmount) => {
    if (!userLevelInfo || !coinsAmount || coinsAmount < 10) return 0;
    const bonusPercent = userLevelInfo.deposit_bonus_percent || 0;
    // Formula: Each coin = $0.1, bonus = (amount * $0.1) / 100 * bonus_percent
    // Simplified: bonus = amount * 0.001 * bonus_percent
    return (coinsAmount * 0.001 * bonusPercent).toFixed(4);
  };

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum deposit is 10 coins');
      return;
    }

    // If Solana is active and wallet connected, use SOL payment
    if (solanaSettings?.is_active && connected) {
      await handleSolDeposit(amount);
      return;
    }

    // Fallback to mock deposit
    setDepositing(true);
    try {
      const res = await axios.post(
        `${API}/wallet/deposit`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deposited ${res.data.coins_added} coins (includes ${res.data.bonus} bonus)!`);
      setShowDeposit(false);
      setDepositAmount('');
      fetchTransactions();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deposit failed');
    } finally {
      setDepositing(false);
    }
  };

  const handleSolDeposit = async (coinsAmount) => {
    const solAmount = coinsAmount / (solanaSettings?.sol_to_coin_rate || 1000);
    
    // Check if user has enough SOL
    if (solBalance < solAmount) {
      toast.error(`Insufficient SOL balance. Need ${solAmount.toFixed(4)} SOL`);
      return;
    }

    // First show waiting for wallet approval
    setSolDepositStep('waiting_wallet');
    setDepositing(true);

    try {
      let result;
      const depositMethod = solanaSettings?.deposit_method || 'contract_deposit';
      const isDevnet = solanaSettings?.network === 'devnet';
      
      // Debug: Log the settings being used
      console.log('Deposit settings:', {
        method: depositMethod,
        program_id: solanaSettings?.program_id,
        treasury: solanaSettings?.admin_wallet,
        network: solanaSettings?.network,
        isDevnet,
        pumpswap_program: solanaSettings?.pumpswap_program,
        pumpswap_pool: solanaSettings?.pumpswap_pool,
        cashback_token_mint: solanaSettings?.cashback_token_mint
      });
      
      if (depositMethod === 'contract_cashback' && solanaSettings?.program_id) {
        // Use smart contract deposit with PumpSwap cashback
        // Pool address is now derived from token mint using canonicalPumpPoolPda
        const cashbackSettings = {
          pumpswapProgram: solanaSettings.pumpswap_program,
          cashbackTokenMint: solanaSettings.cashback_token_mint,
          treasuryAddress: solanaSettings.admin_wallet,
          isDevnet: isDevnet // Pass network info for SDK/CPI switching
        };
        
        console.log('Cashback deposit settings:', cashbackSettings);
        
        // Check if PumpSwap settings are configured (pool is derived from token mint)
        const hasPumpSwapSettings = cashbackSettings.pumpswapProgram && 
                                     cashbackSettings.cashbackTokenMint;
        
        if (!hasPumpSwapSettings) {
          // Fallback to standard deposit if PumpSwap not configured
          console.log('⚠️ PumpSwap not configured, using standard deposit');
          toast.info('PumpSwap not configured. Using standard deposit.', { duration: 3000 });
          
          result = await sendSOLViaContract(
            solAmount, 
            token, 
            solanaSettings.program_id,
            solanaSettings.admin_wallet
          );
        } else {
          // PumpSwap configured - proceed with cashback deposit
          console.log(`✅ Using deposit with cashback via PumpSwap (${isDevnet ? 'Devnet' : 'Mainnet'})`);
          toast.info('Deposit with cashback token...', { duration: 2000 });
          
          result = await sendSOLWithCashback(
            solAmount,
            token,
            solanaSettings.program_id,
            cashbackSettings
          );
        }
      } else if (depositMethod !== 'legacy' && solanaSettings?.program_id) {
        // Use standard smart contract deposit
        result = await sendSOLViaContract(
          solAmount, 
          token, 
          solanaSettings.program_id,
          solanaSettings.admin_wallet  // Treasury address
        );
      } else {
        // Use legacy direct transfer (should not happen now)
        throw new Error('Legacy deposit is no longer supported. Please configure smart contract deposit.');
      }
      
      // After signing, show processing
      setSolDepositStep('processing');
      
      setLastTxSignature(result.signature);
      setSolDepositStep('success');
      
      // Show success message with cashback info if available
      if (result.cashback_token_amount && result.cashback_token_amount > 0) {
        toast.success(
          `${result.coins_credited} coins credited! + ${result.cashback_token_amount.toLocaleString()} cashback tokens`, 
          { duration: 5000 }
        );
      } else {
        toast.success(`${result.coins_credited} coins credited!`);
      }
      
      fetchTransactions();
      refreshUser();
      fetchSolBalance();
    } catch (err) {
      // Extract error message and txInfo if available
      const errorMsg = err.response?.data?.detail || err.message || 'Transaction failed';
      const txInfo = err.txInfo || null;
      
      let errorDetails = {};
      
      if (txInfo) {
        // Detailed on-chain error info
        errorDetails = {
          message: errorMsg,
          signature: txInfo.signature,
          explorer_url: txInfo.explorer_url,
          error_code: txInfo.error_code,
          logs: txInfo.logs || [],
          accounts: txInfo.accounts_used || [],
          timestamp: new Date().toISOString()
        };
      } else {
        // Generic error
        const fullError = typeof err === 'object' ? JSON.stringify(err, Object.getOwnPropertyNames(err), 2) : String(err);
        errorDetails = {
          message: errorMsg,
          details: fullError,
          timestamp: new Date().toISOString()
        };
      }
      
      setDepositError(errorDetails);
      setSolDepositStep('error');
      toast.error(errorMsg);
    } finally {
      setDepositing(false);
    }
  };

  const resetDepositDialog = () => {
    setShowDeposit(false);
    setDepositAmount('');
    setSolDepositStep('amount');
    setLastTxSignature(null);
    setDepositError(null);
  };

  const handleTxClick = (tx) => {
    if ((tx.type === 'gacha' || tx.type === 'sell' || tx.type === 'ticket_reward') && (tx.item_image || tx.item_name)) {
      setSelectedTx(tx);
      setShowItemPreview(true);
    }
  };

  const quickAmounts = [10, 50, 100, 500];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'THE_ONE_ONLY': return 'text-amber-500';
      case 'LEGEND': return 'text-yellow-500';
      case 'EPIC': return 'text-purple-500';
      case 'RARE': return 'text-blue-500';
      default: return 'text-zinc-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <SEOHead page="wallet" />
      <PageHeader title={t('wallet.title')} showBack={true} />

      {/* Balance Cards */}
      <div className="px-5 py-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Coins Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => setActiveTab('coins')}
            className={`relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all ${
              activeTab === 'coins' 
                ? 'bg-gradient-to-br from-yellow-900/50 to-amber-950/50 border-2 border-yellow-500/50' 
                : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700'
            }`}
          >
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-yellow-500/10 rounded-full blur-2xl" />
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
                    <Coins className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-zinc-400 text-xs uppercase tracking-wider">{t('wallet.coins')}</span>
                </div>
                <p className="text-2xl font-bold text-white font-['Chakra_Petch']">
                  {user?.coins?.toFixed(0) || 0}
                </p>
              </div>
              {/* Deposit Button - Centered vertically */}
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  // Refresh settings before opening deposit modal
                  await fetchSolanaSettings();
                  setShowDeposit(true);
                }}
                className="w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-500 flex items-center justify-center transition-all shadow-lg shadow-amber-600/30 hover:scale-105"
                data-testid="deposit-btn"
              >
                <Plus className="w-6 h-6 text-white" />
              </button>
            </div>
          </motion.div>

          {/* Ticket Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            onClick={() => setActiveTab('tickets')}
            className={`relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all ${
              activeTab === 'tickets' 
                ? 'bg-gradient-to-br from-violet-900/50 to-purple-950/50 border-2 border-violet-500/50' 
                : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700'
            }`}
          >
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-violet-500/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Ticket className="w-4 h-4 text-white" />
                </div>
                <span className="text-zinc-400 text-xs uppercase tracking-wider">{t('wallet.tickets')}</span>
              </div>
              <p className="text-2xl font-bold text-white font-['Chakra_Petch']">
                {user?.tickets || 0}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Info */}
      </div>

      {/* Tab Content */}
      <div className="px-5 py-4">
        <h3 className="text-lg font-bold text-white uppercase tracking-wider font-['Chakra_Petch'] mb-4">
          {activeTab === 'coins' ? t('wallet.transactionHistory') : t('wallet.ticketHistory')}
        </h3>

        {/* Coins Transactions */}
        {activeTab === 'coins' && (
          <>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-zinc-900 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <EmptyState
                title={t('wallet.noTransactions')}
                subtitle={t('wallet.makeDepositOrPlay')}
                size={150}
              />
            ) : (
              <div className="space-y-3 pb-24">
                {transactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTxClick(tx)}
                    className={`flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl ${
                      (tx.type === 'gacha' || tx.type === 'sell') && (tx.item_image || tx.item_name)
                        ? 'cursor-pointer hover:bg-zinc-800/50 active:scale-[0.98] transition-all'
                        : tx.tx_id 
                        ? 'cursor-pointer hover:bg-zinc-800/50 active:scale-[0.98] transition-all'
                        : ''
                    }`}
                    data-testid={`tx-${tx.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      tx.type === 'deposit' || tx.type === 'deposit_sol' || tx.type === 'deposit_sol_contract' ? 'bg-green-900/50' :
                      tx.type === 'admin_topup' ? 'bg-purple-900/50' :
                      tx.type === 'sell' ? 'bg-yellow-900/50' :
                      'bg-amber-900/50'
                    }`}>
                      {tx.type === 'deposit' || tx.type === 'deposit_sol' || tx.type === 'deposit_sol_contract' ? (
                        <ArrowDown className="w-5 h-5 text-green-500" />
                      ) : tx.type === 'admin_topup' ? (
                        <Gift className="w-5 h-5 text-purple-400" />
                      ) : tx.type === 'sell' ? (
                        <ArrowUp className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <Coins className="w-5 h-5 text-amber-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium capitalize">
                          {tx.type === 'sell' ? 'Sell' : 
                           tx.type === 'gacha' ? 'Gacha' : 
                           tx.type === 'admin_topup' ? 'Admin Bonus' :
                           tx.type === 'deposit' || tx.type === 'deposit_sol' ? 'Deposit (Legacy)' :
                           tx.type === 'deposit_sol_contract' && tx.deposit_type === 'cashback' ? 'Deposit + Cashback' :
                           tx.type === 'deposit_sol_contract' ? 'Deposit (Contract)' :
                           tx.type}
                        </p>
                        {tx.type === 'admin_topup' && (
                          <span className="px-1.5 py-0.5 bg-purple-900/50 text-purple-300 text-xs rounded">
                            From Admin
                          </span>
                        )}
                        {/* Show SOL amount badge for deposits */}
                        {(tx.type === 'deposit_sol' || tx.type === 'deposit_sol_contract') && tx.sol_amount && (
                          <span className="px-1.5 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">
                            {tx.sol_amount} SOL
                          </span>
                        )}
                        {/* Show cashback token amount badge */}
                        {tx.type === 'deposit_sol_contract' && tx.deposit_type === 'cashback' && tx.cashback_token_amount > 0 && (
                          <span className="px-1.5 py-0.5 bg-purple-900/50 text-purple-400 text-xs rounded flex items-center gap-1">
                            <span className="text-purple-300">+</span>
                            {tx.cashback_token_amount >= 1000000 
                              ? `${(tx.cashback_token_amount / 1000000).toFixed(2)}M` 
                              : tx.cashback_token_amount >= 1000 
                                ? `${(tx.cashback_token_amount / 1000).toFixed(2)}K`
                                : tx.cashback_token_amount.toFixed(2)
                            } ${solanaSettings?.cashback_token_symbol ? `$${solanaSettings.cashback_token_symbol}` : 'Token'}
                          </span>
                        )}
                        {/* Show ticket reward badge */}
                        {tx.type === 'gacha' && tx.ticket_reward > 0 && (
                          <span className="px-1.5 py-0.5 bg-yellow-900/50 text-yellow-400 text-xs rounded">
                            +{tx.ticket_reward} Tickets
                          </span>
                        )}
                      </div>
                      {/* Show description for admin topup */}
                      {tx.type === 'admin_topup' && tx.description && (
                        <p className="text-purple-300 text-xs truncate">{tx.description}</p>
                      )}
                      {/* Show tx_id for deposits */}
                      {(tx.type === 'deposit_sol' || tx.type === 'deposit_sol_contract') && tx.tx_id && (
                        <a 
                          href={`https://solscan.io/tx/${tx.tx_id}${solanaSettings?.network !== 'mainnet-beta' ? '?cluster=devnet' : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-purple-400 hover:text-purple-300 text-xs truncate flex items-center gap-1"
                        >
                          {tx.tx_id.slice(0, 20)}... <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {(tx.type === 'gacha' || tx.type === 'sell') && tx.item_name && (
                        <p className="text-zinc-300 text-xs truncate">{getLocalizedText(tx.item_name, currentLang)}</p>
                      )}
                      {tx.pool_name && (
                        <p className="text-zinc-500 text-xs truncate">{getLocalizedText(tx.pool_name, currentLang)}</p>
                      )}
                      <p className="text-zinc-600 text-xs">{formatDate(tx.created_at)}</p>
                    </div>
                    <div className={`text-right font-bold flex-shrink-0 ${
                      tx.amount > 0 ? 'text-green-500' : 'text-amber-500'
                    }`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(0)}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Ticket Transactions */}
        {activeTab === 'tickets' && (
          <>
            {tokenLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-zinc-900 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : tokenTransactions.length === 0 ? (
              <EmptyState
                title="No Ticket history yet"
                subtitle="Play gacha to earn Tickets!"
                size={150}
              />
            ) : (
              <div className="space-y-3 pb-24">
                {tokenTransactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleTxClick(tx)}
                    className={`flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl cursor-pointer hover:bg-zinc-800/50 active:scale-[0.98] transition-all ${
                      tx.type === 'admin_send' ? 'border-purple-800/30' : ''
                    }`}
                    data-testid={`token-tx-${tx.id}`}
                  >
                    {/* Item thumbnail or Admin icon */}
                    <div className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 ${
                      tx.type === 'admin_send' ? 'bg-purple-900/50' : 'bg-zinc-800'
                    }`}>
                      {tx.type === 'admin_send' ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gift className="w-6 h-6 text-purple-400" />
                        </div>
                      ) : tx.item_image ? (
                        <img src={tx.item_image} alt={getLocalizedText(tx.item_name, currentLang)} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Ticket className="w-6 h-6 text-violet-500" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {tx.type === 'admin_send' ? (
                        <>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{t('wallet.adminBonus')}</p>
                            <span className="px-1.5 py-0.5 bg-violet-900/50 text-violet-300 text-xs rounded">
                              {t('wallet.fromAdmin')}
                            </span>
                          </div>
                          <p className="text-violet-300 text-xs truncate">{tx.description || t('wallet.bonusFromAdmin')}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-white font-medium truncate">{getLocalizedText(tx.item_name, currentLang) || t('wallet.unknownItem')}</p>
                          <p className={`text-xs ${getRarityColor(tx.item_rarity)}`}>
                            {tx.item_rarity?.replace('_', ' ') || t('wallet.unknownItem')}
                          </p>
                        </>
                      )}
                      {tx.pool_name && (
                        <p className="text-zinc-500 text-xs truncate">{getLocalizedText(tx.pool_name, currentLang)}</p>
                      )}
                      <p className="text-zinc-600 text-xs">{formatDate(tx.created_at)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold text-lg ${tx.type === 'admin_send' ? 'text-violet-400' : 'text-violet-400'}`}>+{tx.amount}</p>
                      <p className="text-zinc-500 text-xs">{t('wallet.tickets')}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Deposit Dialog */}
      <Dialog open={showDeposit} onOpenChange={resetDepositDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-xl font-['Chakra_Petch'] uppercase tracking-wider">
              {t('wallet.deposit')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Wallet Status - Show if Solana is active */}
            {solanaSettings?.is_active && (
              <div className="p-3 bg-gradient-to-br from-violet-900/30 to-purple-950/20 border border-violet-800/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <WalletIcon className="w-4 h-4 text-violet-400" />
                    <span className="text-white text-sm font-medium">{t('wallet.solanaWallet')}</span>
                  </div>
                  {connected ? (
                    <span className="flex items-center gap-1 text-green-400 text-xs">
                      <CheckCircle className="w-3 h-3" /> {publicKey?.toBase58()?.slice(0, 4)}...{publicKey?.toBase58()?.slice(-4)}
                    </span>
                  ) : (
                    <span className="text-zinc-500 text-xs">{t('errors.walletNotConnected', { defaultValue: 'Not connected' })}</span>
                  )}
                </div>
                {connected && solBalance !== null && (
                  <div className="mt-2 pt-2 border-t border-violet-800/30">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400 text-xs">{t('wallet.solBalance')}</span>
                      <span className="text-white text-sm font-bold">{solBalance?.toFixed(4)} SOL</span>
                    </div>
                  </div>
                )}
                {!connected && (
                  <p className="text-zinc-500 text-xs mt-2">
                    {t('wallet.connectToTopUp')}
                  </p>
                )}
              </div>
            )}

            {/* Success State */}
            {solDepositStep === 'success' && (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{t('wallet.depositSuccess')}</p>
                  <p className="text-zinc-400 text-sm">{t('wallet.coinsAddedToAccount', { defaultValue: 'Coins has been added to your account' })}</p>
                </div>
                {lastTxSignature && (
                  <a
                    href={`https://solscan.io/tx/${lastTxSignature}${solanaSettings?.network !== 'mainnet-beta' ? '?cluster=devnet' : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm"
                  >
                    View on Solscan <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <Button
                  onClick={resetDepositDialog}
                  className="w-full h-10 bg-zinc-800 hover:bg-zinc-700 text-white"
                >
                  Close
                </Button>
              </div>
            )}

            {/* Error State */}
            {solDepositStep === 'error' && depositError && (
              <div className="py-4 space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <p className="text-white font-bold text-lg">{t('wallet.depositFailed', { defaultValue: 'Deposit Failed' })}</p>
                  <p className="text-red-400 text-sm mt-1">{depositError.message}</p>
                </div>
                
                {/* Explorer Link if available */}
                {depositError.explorer_url && (
                  <a
                    href={depositError.explorer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 text-purple-400 hover:text-purple-300 text-sm"
                  >
                    View on Solscan <ExternalLink className="w-4 h-4" />
                  </a>
                )}

                {/* Action Buttons */}
                <Button
                  onClick={() => {
                    setDepositError(null);
                    setSolDepositStep('amount');
                  }}
                  className="w-full h-10 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('wallet.tryAgain', { defaultValue: 'Try Again' })}
                </Button>
              </div>
            )}

            {/* Waiting Wallet Approval State */}
            {solDepositStep === 'waiting_wallet' && (
              <div className="text-center py-6 space-y-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-900/50 to-violet-900/50 flex items-center justify-center mx-auto relative">
                  <WalletIcon className="w-10 h-10 text-purple-400" />
                  <div className="absolute inset-0 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{t('wallet.waitingWalletApproval', { defaultValue: 'Waiting for Wallet...' })}</p>
                  <p className="text-zinc-400 text-sm mt-1">{t('wallet.pleaseConfirmInWallet', { defaultValue: 'Please confirm the transaction in your wallet' })}</p>
                  <p className="text-purple-400 text-xs mt-3 animate-pulse">{t('wallet.checkYourWallet', { defaultValue: 'Check your Phantom/Solflare wallet' })}</p>
                </div>
              </div>
            )}

            {/* Processing State */}
            {solDepositStep === 'processing' && (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto">
                  <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{t('wallet.processing')}</p>
                  <p className="text-zinc-400 text-sm">{t('wallet.transactionInProgress', { defaultValue: 'Transaction in progress...' })}</p>
                </div>
              </div>
            )}

            {/* Amount Input State */}
            {solDepositStep === 'amount' && (
              <>
                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">{t('wallet.quantityCoins')}</label>
                  <Input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder={t('common.enterAmount', { defaultValue: 'Enter amount...' })}
                    className="h-14 bg-zinc-800 border-zinc-700 text-white text-xl text-center font-bold"
                    data-testid="deposit-amount"
                  />
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {quickAmounts.map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      onClick={() => setDepositAmount(amount.toString())}
                      className="h-10 border-zinc-700 text-white hover:bg-zinc-800"
                    >
                      {amount}
                    </Button>
                  ))}
                </div>

                {/* SOL Payment Info */}
                {solanaSettings?.is_active && connected && depositAmount && parseFloat(depositAmount) >= 10 && (
                  <div className="p-3 bg-purple-900/20 border border-purple-800/30 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400 text-sm">{t('wallet.rate')}</span>
                      <span className="text-white text-sm">1 SOL = {solanaSettings.sol_to_coin_rate} {t('wallet.coins')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400 text-sm">{t('wallet.youWillReceive')}</span>
                      <span className="text-green-400 font-bold">
                        {parseFloat(depositAmount).toFixed(0)} {t('wallet.coins')}
                      </span>
                    </div>
                    {/* Show cashback info if deposit method is contract_cashback */}
                    {solanaSettings?.deposit_method === 'contract_cashback' && solanaSettings?.cashback_token_symbol && (
                      <div className="flex items-center justify-between text-purple-300">
                        <span className="text-zinc-400 text-sm">Cashback ({solanaSettings?.cashback_bps ? (solanaSettings.cashback_bps / 100).toFixed(0) : 1}%)</span>
                        <span className="text-purple-400 font-medium text-sm">
                          + ${solanaSettings.cashback_token_symbol}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-purple-800/30 pt-2">
                      <span className="text-zinc-400 text-sm">{t('wallet.pay')}</span>
                      <span className="text-purple-400 font-bold">
                        {(parseFloat(depositAmount) / solanaSettings.sol_to_coin_rate).toFixed(4)} SOL
                      </span>
                    </div>
                    {solBalance < (parseFloat(depositAmount) / solanaSettings.sol_to_coin_rate) && (
                      <div className="flex items-center gap-2 text-amber-400 text-xs mt-2">
                        <AlertCircle className="w-4 h-4" />
                      <span>{t('wallet.insufficientBalance')}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Non-SOL or not connected info */}
                {(!solanaSettings?.is_active || !connected) && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-sm">{t('wallet.minDeposit')}</p>
                    {depositAmount && parseFloat(depositAmount) >= 10 && (
                      <p className="text-white text-sm mt-2">
                        {t('wallet.youWillReceive')}: <span className="font-bold text-yellow-500">
                          {parseFloat(depositAmount).toFixed(0)} {t('wallet.coins').toLowerCase()}
                        </span>
                      </p>
                    )}
                  </div>
                )}

                <Button
                  onClick={handleDeposit}
                  disabled={
                    depositing || 
                    !depositAmount || 
                    parseFloat(depositAmount) < 10 ||
                    (solanaSettings?.is_active && connected && solBalance < (parseFloat(depositAmount) / (solanaSettings?.sol_to_coin_rate || 1000)))
                  }
                  className={`w-full h-12 font-bold uppercase tracking-wider rounded-xl disabled:opacity-50 ${
                    solanaSettings?.is_active && connected 
                      ? 'bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700' 
                      : 'bg-amber-600 hover:bg-amber-700'
                  } text-white`}
                  data-testid="confirm-deposit"
                >
                  {depositing ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('wallet.processing')}
                    </span>
                  ) : solanaSettings?.is_active && connected ? (
                    t('wallet.payWithSOL')
                  ) : (
                    t('common.confirm')
                  )}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Preview Dialog */}
      <Dialog open={showItemPreview} onOpenChange={setShowItemPreview}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm p-0 overflow-hidden">
          <div className="relative">
            <button
              onClick={() => setShowItemPreview(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {selectedTx?.item_image ? (
              <div className="aspect-square bg-zinc-800">
                <img
                  src={selectedTx.item_image}
                  alt={getLocalizedText(selectedTx.item_name, currentLang) || 'Item'}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-square bg-zinc-800 flex items-center justify-center">
                <Ticket className="w-20 h-20 text-zinc-600" />
              </div>
            )}

            <div className="p-4 space-y-3">
              <div>
                <p className="text-zinc-400 text-xs uppercase tracking-wider">
                  {selectedTx?.type === 'sell' ? t('inventory.sold') : selectedTx?.type === 'ticket_reward' ? t('wallet.ticketReward', { defaultValue: 'Ticket Reward' }) : t('inventory.itemReceived', { defaultValue: 'Item Received' })}
                </p>
                <h3 className="text-white text-lg font-bold font-['Chakra_Petch']">
                  {getLocalizedText(selectedTx?.item_name, currentLang) || 'Unknown Item'}
                </h3>
                {selectedTx?.item_rarity && (
                  <p className={`text-sm ${getRarityColor(selectedTx.item_rarity)}`}>
                    {selectedTx.item_rarity.replace('_', ' ')}
                  </p>
                )}
              </div>

              {selectedTx?.pool_name && (
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wider">Pool</p>
                  <p className="text-zinc-300 text-sm">{getLocalizedText(selectedTx.pool_name, currentLang)}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wider">
                    {selectedTx?.type === 'sell' ? t('inventory.sellResult', { defaultValue: 'Sell Result' }) : selectedTx?.type === 'ticket_reward' ? t('wallet.ticketsEarned', { defaultValue: 'Tickets Earned' }) : t('common.cost')}
                  </p>
                  {selectedTx?.type === 'ticket_reward' ? (
                    <p className="text-lg font-bold text-yellow-400">
                      +{selectedTx?.amount} {t('wallet.tickets')}
                    </p>
                  ) : (
                    <p className={`text-lg font-bold ${
                      selectedTx?.amount > 0 ? 'text-green-500' : 'text-amber-500'
                    }`}>
                      {selectedTx?.amount > 0 ? '+' : ''}{selectedTx?.amount?.toFixed(0)} {t('wallet.coins')}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.time')}</p>
                  <p className="text-zinc-300 text-sm">
                    {selectedTx && formatDate(selectedTx.created_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};
